import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ShieldCheck, Lock, Mail, Loader2, Package, Inbox, LogOut, Phone, ShoppingBag } from "lucide-react";
import PaintEdges from "../components/PaintEdges";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { formatPrice } from "../mock";
import ProductManager from "../components/admin/ProductManager";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const { checkAuth, logout: ctxLogout } = useAuth();
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  const loadData = useCallback(async () => {
    setDataLoading(true);
    try {
      const [r, o] = await Promise.all([
        axios.get(`${API}/admin/requests`, { withCredentials: true }),
        axios.get(`${API}/admin/orders`, { withCredentials: true }),
      ]);
      setRequests(Array.isArray(r.data) ? r.data : []);
      setOrders(Array.isArray(o.data) ? o.data : []);
      setAuthed(true);
    } catch (err) {
      const s = err?.response?.status;
      if (s === 401 || s === 403) setAuthed(false);
      else toast.error("Daten konnten nicht geladen werden.");
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { email, password }, { withCredentials: true });
      await checkAuth();
      toast.success("Willkommen im Admin-Bereich!");
      await loadData();
    } catch (err) {
      toast.error(err?.response?.status === 429 ? "Zu viele Versuche – bitte später erneut." : "Ungültige Zugangsdaten.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await ctxLogout();
    setAuthed(false);
    setEmail("");
    setPassword("");
  };

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  if (!authed) {
    return (
      <main className="relative overflow-hidden bg-gradient-to-b from-[#cdebfb] via-[#e3f4fd] to-white min-h-[calc(100vh-76px)] flex items-center">
        <PaintEdges from="#0e6da8" via="#1b9ad6" to="#6ec6ee" opacity={0.8} />
        <div className="relative mx-auto w-full max-w-md px-6">
          <div className="card-soft !border-sky-200 p-8 sm:p-10">
            <div className="flex justify-center mb-6">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#1273a8] to-[#1b9ad6] flex items-center justify-center shadow-lg">
                <ShieldCheck className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-center font-display text-3xl font-extrabold">
              <span className="text-[#1273a8]">Admin</span> <span className="text-[#1b9ad6]">Login</span>
            </h1>
            <p className="text-center text-slate-500 mt-2">Nur für den Shop-Betreiber.</p>
            <form onSubmit={login} className="mt-8 space-y-4">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#1b9ad6]" />
                <input type="email" required className="input-soft !pl-12 h-12 !border-sky-200"
                  placeholder="E-Mail-Adresse" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#1b9ad6]" />
                <input type="password" required className="input-soft !pl-12 h-12 !border-sky-200"
                  placeholder="Passwort" value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <button type="submit" disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full h-12 text-white font-bold bg-gradient-to-r from-[#1273a8] to-[#1b9ad6] shadow-[0_8px_20px_rgba(27,154,214,0.35)] hover:-translate-y-0.5 transition-transform disabled:opacity-60">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />}
                Einloggen
              </button>
            </form>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="relative overflow-hidden bg-gradient-to-b from-[#dff2fc] via-[#ecf9ee] to-[#fff6ea] min-h-[calc(100vh-76px)]">
      <PaintEdges from="#1b9ad6" via="#7cc24a" to="#f7941e" opacity={0.5} />
      <div className="relative mx-auto max-w-[1100px] px-4 sm:px-8 py-14">
        <div className="flex items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#1273a8] to-[#1b9ad6] flex items-center justify-center shadow-lg">
              <ShieldCheck className="h-7 w-7 text-white" />
            </div>
            <h1 className="font-display text-3xl font-extrabold">
              <span className="text-[#1b7fc1]">Admin</span> <span className="text-[#6fbf3f]">Dashboard</span>
            </h1>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 rounded-full px-5 h-11 font-bold text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors">
            <LogOut className="h-4 w-4" /> Abmelden
          </button>
        </div>

        <Tabs defaultValue="produkte">
          <TabsList className="bg-white/70 rounded-full p-1.5 h-auto shadow-sm flex-wrap justify-start">
            <TabsTrigger value="anfragen" className="rounded-full px-6 py-2.5 font-bold data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#1b9ad6] data-[state=active]:to-[#1273a8] data-[state=active]:text-white">
              <Inbox className="h-4 w-4 mr-2" /> Anfragen
              <span className="ml-2 h-5 min-w-5 rounded-full bg-[#f7941e] text-white text-xs flex items-center justify-center px-1">{requests.length}</span>
            </TabsTrigger>
            <TabsTrigger value="bestellungen" className="rounded-full px-6 py-2.5 font-bold data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#63b93c] data-[state=active]:to-[#3f8f2a] data-[state=active]:text-white">
              <Package className="h-4 w-4 mr-2" /> Bestellungen
              <span className="ml-2 h-5 min-w-5 rounded-full bg-[#f7941e] text-white text-xs flex items-center justify-center px-1">{orders.length}</span>
            </TabsTrigger>
            <TabsTrigger value="produkte" data-testid="admin-tab-produkte" className="rounded-full px-6 py-2.5 font-bold data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#f7941e] data-[state=active]:to-[#e8760a] data-[state=active]:text-white">
              <ShoppingBag className="h-4 w-4 mr-2" /> Produktverwaltung
            </TabsTrigger>
          </TabsList>

          <TabsContent value="anfragen" className="mt-6">
            <div className="card-soft p-6 sm:p-8">
              {dataLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 text-[#1b9ad6] animate-spin" /></div>
              ) : requests.length === 0 ? (
                <p className="text-center text-slate-500 py-12">Noch keine Anfragen eingegangen.</p>
              ) : (
                <div className="space-y-4">
                  {requests.map((r) => (
                    <div key={r.request_id} className="rounded-2xl border border-slate-100 p-5">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className={`rounded-full px-3 py-1 text-xs font-extrabold text-white ${r.type === "cosplay" ? "bg-[#3f8f2a]" : "bg-[#f7941e]"}`}>
                          {r.type === "cosplay" ? "Cosplay-Anfrage" : "3D-Druck-Anfrage"}
                        </span>
                        <span className="text-sm text-slate-400">{fmtDate(r.created_at)}</span>
                      </div>
                      <p className="mt-3 font-bold text-slate-800">{r.name}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-500 mt-1">
                        <span className="flex items-center gap-1.5"><Mail className="h-4 w-4" /> {r.email}</span>
                        {r.phone && <span className="flex items-center gap-1.5"><Phone className="h-4 w-4" /> {r.phone}</span>}
                        <span>{r.num_images} Bild(er)</span>
                      </div>
                      <p className="mt-3 text-slate-600 whitespace-pre-wrap">{r.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="bestellungen" className="mt-6">
            <div className="card-soft p-6 sm:p-8">
              {dataLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 text-[#1b9ad6] animate-spin" /></div>
              ) : orders.length === 0 ? (
                <p className="text-center text-slate-500 py-12">Noch keine bezahlten Bestellungen.</p>
              ) : (
                <div className="space-y-4">
                  {orders.map((o) => (
                    <div key={o.session_id} className="rounded-2xl border border-slate-100 p-5">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="rounded-full px-3 py-1 text-xs font-extrabold text-white bg-[#3f8f2a]">Bezahlt</span>
                        <span className="text-sm text-slate-400">{fmtDate(o.created_at)}</span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(o.items || []).map((it, idx) => (
                          <span key={`${o.session_id}-${idx}`} className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-1 text-sm text-slate-700">{it}</span>
                        ))}
                      </div>
                      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                        <span className="text-sm text-slate-500">
                          Versand: <strong className="text-slate-700">{o.shipping?.toUpperCase()}</strong>
                          {o.discount_code && <span className="ml-3 text-[#3f8f2a] font-semibold">Gutschein: {o.discount_code}</span>}
                        </span>
                        <span className="font-display text-xl font-extrabold text-slate-900">{formatPrice(o.amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="produkte" className="mt-6">
            <div className="card-soft p-6 sm:p-8">
              <ProductManager />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
};

export default Admin;
